import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hook for real-time notifications via SSE + polling fallback.
 * Returns: { unreadCount, notifications, markRead, markAllRead, refresh }
 */
export const useRealtimeNotifications = (enabled = true) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const eventSourceRef = useRef(null);
  const pollingRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API_URL}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(res.data.count || 0);
    } catch {}
  }, []);

  const fetchRecent = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API_URL}/notifications/recent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
    } catch {}
  }, []);

  const refresh = useCallback(() => {
    fetchUnreadCount();
    fetchRecent();
  }, [fetchUnreadCount, fetchRecent]);

  // SSE Connection
  useEffect(() => {
    if (!enabled) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    // Try SSE connection
    const connectSSE = () => {
      try {
        const evtSource = new EventSource(`${API_URL}/notifications/stream?token=${token}`);

        evtSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'ping' || data.type === 'connected') return;

            // New notification received
            if (data.type === 'announcement') {
              setUnreadCount(prev => prev + 1);
              setNotifications(prev => [data, ...prev].slice(0, 20));

              // Show toast notification
              const typeEmoji = {
                urgent: '',
                info: '',
                announcement: '',
                schedule_change: '',
              };
              toast(data.message || 'Nouvelle notification', {
                description: data.author_name ? `Par ${data.author_name}` : undefined,
                duration: 5000,
              });
            }
          } catch {}
        };

        evtSource.onerror = () => {
          evtSource.close();
          // Fallback to polling
          if (!pollingRef.current) {
            pollingRef.current = setInterval(refresh, 15000);
          }
        };

        eventSourceRef.current = evtSource;
      } catch {
        // SSE not supported, use polling
        if (!pollingRef.current) {
          pollingRef.current = setInterval(refresh, 15000);
        }
      }
    };

    // Initial fetch
    refresh();

    // Start SSE (with small delay to let auth settle)
    const timer = setTimeout(connectSSE, 1000);

    // Also poll periodically as backup
    pollingRef.current = setInterval(refresh, 30000);

    return () => {
      clearTimeout(timer);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [enabled, refresh]);

  const markRead = useCallback(async (notifId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/notifications/${notifId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  }, []);

  return { unreadCount, notifications, markRead, markAllRead, refresh };
};

export default useRealtimeNotifications;
